//Main component that sumup having all the product cards their status and other functionalities.

// Hooks
import React, { useEffect, useState } from "react";
import { useTitle } from "../../../Shared/useTitle";

// styles
import Styles from "./productCatalog.module.scss";
import emptyStateStyle from "../../../Shared/empty-state/emptyState.module.scss";

// Components
import ServiceCard from "./service-card/serviceCard";
import FilterTabs from "./filter-tabs/filterTabs";
import PendingApproval from "./pending-approval/pendingApproval";
import Loading from "../../../Shared/loading/loading";
import Toast from "../../../Shared/toast/toast";
import TrialActivatedPopUp from "./trial-activated-pop-up/trialActivatedPopUp";
import Dropdown from "../../../Shared/dropdown/dropdown";
import EmptyState from "../../../Shared/empty-state/emptyState";
import { testIdProps } from "../../../utils/testIdProps";

// Constants
import { ALL } from "../../../constants/filters";
import { ACCENTCOLOR } from "../../../Shared/colors";
import SearchImage from "../../../assets/images/search.png";
import { noContent } from "../../../constants/emptyStatesVariables";

// Functions
import { customEvent } from "../../../utils/ga4";
import { callApi } from "../../../api/fetch";
import {
  subscribeOrganisationProduct,
  unsubscribeOrganisationProduct,
} from "../../../api/products";
import {
  ensureArray,
  getStoredOrganisationId,
  productPID,
  productName,
} from "../../../utils/dashboardMappers";
import { PRODUCT_SUBSCRIPTION_STATUS } from "../../../constants/product";

function ProductCatalog() {
  const org_id = getStoredOrganisationId();
  const [dropdownFilter, setDropdownFilter] = useState("");
  const [selectedProductType, setSelectedProductType] = useState("");
  const [products, setProducts] = useState<any>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [togglePendingStatePopUp, setTogglePendingStatePopUp] =
    useState(false);
  const [productCategories, setProductCategories] = useState(["All"]);
  const [toggleTrialTransactionPopUp, setToggleTrialTransactionPopUp] =
    useState({
      trial_transaction: 0,
      showPopUp: false,
    });
  const [cardLoading, setCardLoading] = useState({
    serviceName: "",
    isLoading: false,
  });

  const statusArray = [
    "All Products",
    "Not Subscribed",
    "Already Subscribed",
    "Pending Approval",
  ];
  const filteredProducts = products.filter((response: any) => {
    return filterConditions(response);
  });
  const emptyState = (
    <div
      className={`${Styles.emptyState} d-flex align-items-center justify-content-center`}
    >
      <EmptyState type={noContent}>
        <div className={"py-3"}>
          <p className={`${emptyStateStyle.emptyStateHeadingText} mb-0`}>
            No Products found
          </p>
        </div>
      </EmptyState>
    </div>
  );

  useEffect(() => {
    async function getProducts() {
      setLoading(true);
      try {
        const response = await callApi("/products", {
          method: "GET",
        });
        const productsList = ensureArray<any>(response);
        const categories = productsList.map((product: any) => product.category);
        setProductCategories((productCategories) =>
          productCategories.concat(
            categories.filter(
              (category: string, index: number) =>
                categories.indexOf(category) === index
            )
          )
        );
        setProducts(productsList);
        setLoading(false);
        setCardLoading((cardLoading) => ({
          ...cardLoading,
          serviceName: "",
          isLoading: false,
        }));
      } catch (err) {
        setLoading(false);
        setCardLoading((cardLoading) => ({
          ...cardLoading,
          serviceName: "",
          isLoading: false,
        }));
        toastTimeout(true, "error", (err as Error).message);
        // TODO: add sentry error
      }
    }
    getProducts();
  }, []);

  function toastTimeout(
    showToastValue: boolean,
    toastType: "success" | "error",
    toastMessage: string | string[],
    heading?: string
  ) {
    if (!showToastValue) return;

    // Show toast via react-toastify
    Toast(
      toastType,
      toastMessage,
      heading || (toastType === "success" ? "Success" : "Error")
    );
  }

  function updateProducts(updatedProduct: any) {
    setProducts(
      products.map((product: any) => {
        if (productPID(product) === productPID(updatedProduct?.product ?? updatedProduct)) {
          return {
            ...product,
            subscription_status: updatedProduct?.subscription_status,
          };
        }
        return product;
      })
    );
  }

  async function handleSubscribeService(
    productName: string,
    id: string,
    category: string
  ) {
    setCardLoading((cardLoading) => ({
      ...cardLoading,
      serviceName: productName,
      isLoading: true,
    }));
    try {
      const response = await subscribeOrganisationProduct(org_id, id);

      setCardLoading((cardLoading) => ({
        ...cardLoading,
        serviceName: "",
        isLoading: false,
      }));

      if (!response?.length) {
        toastTimeout(true, "error", "No products were subscribed. Check your plan or product selection.");
        return;
      }

      setToggleTrialTransactionPopUp((toggleTrialTransactionPopUp) => ({
        ...toggleTrialTransactionPopUp,
        trial_transaction: response?.[0]?.test_credit,
      }));

      updateProducts(response[0]);

      if (!response?.[0]?.product?.is_approval_required) {
        setToggleTrialTransactionPopUp((toggleTrialTransactionPopUp) => ({
          ...toggleTrialTransactionPopUp,
          showPopUp: true,
        }));
        customEvent(
          `${category}_Product`,
          "Product_subscribed",
          `${productName} subscribed`
        );
        return;
      }

      if (response?.[0]?.product?.is_approval_required) {
        setTogglePendingStatePopUp(true);

        customEvent(
          `${category}_Product`,
          "Product_Subscribed",
          `${productName} subscribed`
        );
        return;
      }
    } catch (err) {
      setCardLoading((cardLoading) => ({
        ...cardLoading,
        serviceName: "",
        isLoading: false,
      }));
      toastTimeout(true, "error", (err as Error).message);
    }
  }

  async function unSubscribeService(
    productName: string,
    id: string,
    category: string
  ) {
    setCardLoading((cardLoading) => ({
      ...cardLoading,
      serviceName: productName,
      isLoading: true,
    }));
    try {
      const response = await unsubscribeOrganisationProduct(org_id, id);

      setCardLoading((cardLoading) => ({
        ...cardLoading,
        serviceName: "",
        isLoading: false,
      }));

      // callApi resolves (instead of throwing) for 404/error envelopes, so a
      // successful unsubscribe must come back as the updated product array.
      if (!response?.length) {
        toastTimeout(
          true,
          "error",
          (response as { message?: string })?.message ||
            "Failed to unsubscribe product. Please try again."
        );
        return;
      }

      updateProducts(response[0]);
      toastTimeout(true, "success", "Product unsubscribed successfully");
      customEvent(
        `${category}_Product`,
        "Product_UnSubscribed",
        `${productName} unsubscribed`
      );
    } catch (err) {
      setCardLoading((cardLoading) => ({
        ...cardLoading,
        serviceName: "",
        isLoading: false,
      }));
      toastTimeout(true, "error", (err as Error).message);
    }
    return;
  }

  function toggleTab(value: string) {
    if (value === ALL) {
      return setSelectedProductType("");
    } else if (selectedProductType === value) {
      return setSelectedProductType("");
    }
    setSelectedProductType(value);
    return;
  }

  function filterConditions(response: any) {
    if (dropdownFilter !== "") {
      if (dropdownFilter === "Pending Approval") {
        return (
          response?.category?.includes(selectedProductType) &&
          productName(response)
            .toLowerCase()
            .includes(searchTerm.trim().toLowerCase()) &&
          response?.subscription_status?.toUpperCase() ===
            PRODUCT_SUBSCRIPTION_STATUS.PENDING_APPROVAL
        );
      } else if (dropdownFilter === "Already Subscribed") {
        return (
          response?.category?.includes(selectedProductType) &&
          productName(response)
            .toLowerCase()
            .includes(searchTerm.trim().toLowerCase()) &&
          response?.subscription_status?.toUpperCase() ===
            PRODUCT_SUBSCRIPTION_STATUS.SUBSCRIBED
        );
      } else if (dropdownFilter === "Not Subscribed") {
        return (
          response?.category?.includes(selectedProductType) &&
          productName(response)
            .toLowerCase()
            .includes(searchTerm.trim().toLowerCase()) &&
          response?.subscription_status?.toUpperCase() ===
            PRODUCT_SUBSCRIPTION_STATUS.UNSUBSCRIBED
        );
      }
    }
    return (
      response?.category?.includes(selectedProductType) &&
      productName(response)
        ?.toLowerCase()
        ?.includes(searchTerm?.trim()?.toLowerCase())
    );
  }
  useTitle("Zoop Customer Platform | Products ");
  return (
    <div style={{ backgroundColor: "#F3F4F6", height: "100vh" }}>
      {loading ? (
        <div
          style={{
            height: "80vh",
          }}
          className="d-flex align-items-center justify-content-center"
        >
          <Loading loadingColor={ACCENTCOLOR} />
        </div>
      ) : (
        <div
          className="container-fluid px-4"
          style={{ backgroundColor: "#F3F4F6" }}
        >
          <div className="ml-auto pt-4 d-flex align-items-center justify-content-between">
            <span></span>
            <div
              style={{ width: "400px" }}
              className={`${Styles.inputWrapper} d-flex align-items-center pl-2`}
            >
              <div className="px-2">
                <img
                  src={SearchImage}
                  alt="search"
                  width="20px"
                  style={{ marginBottom: "3px" }}
                />
              </div>
              <div className="flex-grow-1">
                <input
                  {...testIdProps("product-catalog-search")}
                  type="text"
                  className={Styles.formControl}
                  placeholder="Search product here"
                  value={searchTerm}
                  onChange={(event: any) => {
                    setSearchTerm(event.target.value);
                  }}
                />
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-between mb-4 mt-3">
            <div
              className={`d-flex align-items-end justify-content-start ${
                Styles.filterTabContainer || ""
              }`}
            >
              {productCategories.map((category: any, index: number) => {
                return (
                  <div key={`${category}-${index}`} className="mr-4">
                    <FilterTabs
                      filteredString={selectedProductType}
                      category={category}
                      click={(value: any) => toggleTab(value)}
                      hideCross={category === ALL}
                    />
                  </div>
                );
              })}
            </div>
            <div className={Styles.dropdownContainer}>
              <div className="px-2">
                <Dropdown
                  id="product-catalog-filter-all"
                  default_value="All Products"
                  optionsArray={statusArray}
                  click={(value: any) => {
                    if (value === "All Products") {
                      setDropdownFilter("");
                      return;
                    }
                    setDropdownFilter(value);
                  }}
                />
              </div>
            </div>
          </div>
          <div className="container-fluid">
            <div className="row no-gutters pb-3">
              {filteredProducts.length !== 0
                ? filteredProducts.map((product: any) => {
                    return (
                      <div
                        key={productPID(product)}
                        className="col-xl-3 col-md-6"
                        style={{
                          padding: "9px",
                        }}
                      >
                        <ServiceCard
                          isLoading={cardLoading}
                          productDetails={product}
                          click={(value: string) =>
                            value === "subscribe"
                              ? handleSubscribeService(
                                  productName(product),
                                  productPID(product),
                                  product.category
                                )
                              : unSubscribeService(
                                  productName(product),
                                  productPID(product),
                                  product.category
                                )
                          }
                        />
                      </div>
                    );
                  })
                : emptyState}
            </div>
          </div>
        </div>
      )}
      {togglePendingStatePopUp ? (
        <PendingApproval onConfirm={() => setTogglePendingStatePopUp(false)} />
      ) : null}
      {toggleTrialTransactionPopUp.showPopUp ? (
        <TrialActivatedPopUp
          trial_transaction={toggleTrialTransactionPopUp.trial_transaction}
          onConfirm={() => {
            setToggleTrialTransactionPopUp((toggleTrialTransactionPopUp) => ({
              ...toggleTrialTransactionPopUp,
              showPopUp: false,
            }));
          }}
        />
      ) : null}
    </div>
  );
}

export default ProductCatalog;
